// src/data/fishIQCatchabilityFacts.ts
// Evidence-backed catchability overlays for FishIQ growth regions.
// These facts refine broad editorial seeds without changing the base catalog.
// They are intentionally conservative: habitat + water + region must agree
// before FishIQ treats a species as a strong local target.

export type FishIQCatchabilityContext = {
  lat?: number | null;
  lon?: number | null;
  waterName?: string | null;
  waterType?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
};

type GeoBox = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

type CatchabilityFact = {
  ids?: string[];
  scientificNames?: string[];
  commonNames?: string[];
  summary?: string;
  habitats?: string[];
  waterRealms?: string[];
  bestBaits?: string[];
  bestTechniques?: string[];
  bestTimesOfDay?: string[];
  activeTimes?: string[];
  structureTags?: string[];
  behaviorTags?: string[];
  depthPreference?: string;
  feedingProfile?: { primaryPrey?: string[]; notes?: string };
  speciesKnowledge?: {
    habitatFact?: string;
    feedingFact?: string;
    timingFact?: string;
    tacticFact?: string;
    seasonalMovementFact?: string;
  };
  publicSourceRefs?: string[];
  geoBoxes?: GeoBox[];
  geoPenaltyOutside?: number;
  geoReasonOutside?: string;
};

const clean = (value: unknown) => String(value ?? '').trim();
const key = (value: unknown) => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();

const uniq = <T,>(items: T[], keyFn: (value: T) => string = (value) => key(value)): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items || []) {
    const k = keyFn(item);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
};

const FACTS: CatchabilityFact[] = [
  {
    ids: ['rohu', 'rohu_india'], scientificNames: ['Labeo rohita'],
    summary: 'A river and reservoir carp that commonly feeds along littoral and mid-water zones on detritus, periphyton, plant material, and small invertebrates.',
    habitats: ['river', 'lake', 'reservoir', 'pond'], waterRealms: ['freshwater'],
    bestBaits: ['Dough Bait', 'Bread', 'Bran/Cake Groundbait', 'Plant-based Paste'],
    bestTechniques: ['Fish littoral feeding lanes', 'Present bait near mid-water or margins', 'Use light groundbait to hold fish'],
    bestTimesOfDay: ['day', 'dawn', 'dusk'], activeTimes: ['day', 'dawn', 'dusk'],
    structureTags: ['littoral edges', 'vegetation', 'gentle current', 'feeding flats'], behaviorTags: ['grazing', 'diurnal'],
    depthPreference: 'littoral and mid-water feeding zones, especially productive margins',
    feedingProfile: { primaryPrey: ['detritus', 'periphyton', 'plant material', 'small invertebrates'], notes: 'Diurnal feeder; focus on productive margins and food-rich littoral water rather than predator-style presentations.' },
    speciesKnowledge: {
      habitatFact: 'Rohu naturally inhabit rivers and are also widely stocked in reservoirs and culture waters; non-spawning adults commonly feed in littoral zones.',
      feedingFact: 'Adults feed mainly on detritus and plant material with small invertebrates also contributing.',
      timingFact: 'Rohu are primarily diurnal; daylight feeding is the strongest general timing signal.',
      tacticFact: 'Use small plant/dough presentations around productive margins and hold fish with modest groundbait rather than chasing them with lures.',
      seasonalMovementFact: 'Monsoon flooding can change distribution and spawning movement; local water level and current matter more than a temperate season label.',
    },
    publicSourceRefs: ['FishBase Ecology: Labeo rohita', 'ICAR-CIFRI Indian major carp fisheries'],
  },
  {
    ids: ['catla', 'catla_india'], scientificNames: ['Labeo catla', 'Catla catla'],
    summary: 'A major Indian carp of rivers, lakes, reservoirs, and culture ponds that feeds mainly in surface and mid-water zones on zooplankton and other small food items.',
    habitats: ['river', 'lake', 'reservoir', 'pond'], waterRealms: ['freshwater'],
    bestBaits: ['Dough Bait', 'Bread', 'Bran/Cake Groundbait', 'Small Natural Bait'],
    bestTechniques: ['Fish surface-to-mid water', 'Use suspended or float presentations', 'Build a feeding lane with light groundbait'],
    bestTimesOfDay: ['day', 'dawn', 'dusk'], activeTimes: ['day', 'dawn', 'dusk'],
    structureTags: ['open water edges', 'feeding flats', 'reservoir margins'], behaviorTags: ['surface feeder', 'mid-water feeder'],
    depthPreference: 'surface-to-mid water over productive lake, river, and reservoir zones',
    feedingProfile: { primaryPrey: ['zooplankton', 'phytoplankton', 'insects', 'detritus'], notes: 'A surface/mid-water feeder; suspended presentations generally fit better than bottom-only tactics.' },
    speciesKnowledge: {
      habitatFact: 'Adults occur in rivers, lakes, reservoirs, and culture ponds.',
      feedingFact: 'Catla are surface and mid-water feeders; zooplankton is a major food component.',
      timingFact: 'Daylight feeding is a reasonable general window; local feeding activity and wind-driven plankton movement matter.',
      tacticFact: 'Keep bait suspended or near the upper half of the water column and follow visible feeding or windblown food lanes.',
    },
    publicSourceRefs: ['FishBase Biology: Labeo catla', 'ICAR-CIFRI Indian major carp fisheries'],
  },
  {
    ids: ['golden_mahseer_india', 'golden_mahseer'], scientificNames: ['Tor putitora'],
    summary: 'A Himalayan and north-Indian river fish associated with rapid rocky streams, river pools, and connected lakes; it is not a generic all-India warmwater species.',
    habitats: ['river', 'creek', 'lake'], waterRealms: ['freshwater'],
    bestBaits: ['Spinner', 'Spoon', 'Crankbait', 'Natural Insect/Fruit Bait'],
    bestTechniques: ['Work rocky runs and pool tails', 'Cast current seams', 'Cover oxygenated river pools'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'], activeTimes: ['dawn', 'day', 'dusk'],
    structureTags: ['rocky runs', 'river pools', 'boulders', 'gravel'], behaviorTags: ['current-oriented', 'omnivorous'],
    depthPreference: 'rapid rocky streams, pools, and connected lake/river edges',
    feedingProfile: { primaryPrey: ['aquatic insects', 'fish', 'zooplankton', 'plant matter'], notes: 'Current, oxygen, rocks, and natural drift are stronger clues than generic shoreline structure.' },
    speciesKnowledge: {
      habitatFact: 'Golden mahseer inhabit streams, river pools, lakes, and rapid rocky water in the Himalayan/northern part of their range.',
      feedingFact: 'They are omnivorous, taking aquatic insects, fish, zooplankton, and plant matter.',
      tacticFact: 'Prioritize current seams, boulder pockets, pool heads and tails, and natural drift lanes.',
    },
    publicSourceRefs: ['FishBase Biology: Tor putitora'],
    geoBoxes: [{ minLat: 20, maxLat: 35.8, minLon: 72, maxLon: 97.5 }],
    geoPenaltyOutside: 34,
    geoReasonOutside: 'golden mahseer is a northern/Himalayan profile, not a generic all-India target',
  },
  {
    ids: ['deccan_mahseer_india', 'deccan_mahseer'], scientificNames: ['Tor khudree'],
    summary: 'A peninsular Indian mahseer of cool, fast-flowing rocky streams and rivers, with some lake/reservoir populations including stocked waters.',
    habitats: ['river', 'creek', 'reservoir', 'lake'], waterRealms: ['freshwater'],
    bestBaits: ['Spinner', 'Crankbait', 'Natural Insect Bait', 'Shrimp/Prawn'],
    bestTechniques: ['Work rocky current', 'Fish pool heads and tails', 'Cover boulder seams quietly'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'], activeTimes: ['dawn', 'day', 'dusk'],
    structureTags: ['rocky streams', 'boulders', 'pool tails', 'fast current'], behaviorTags: ['current-oriented', 'omnivorous'],
    depthPreference: 'cool rocky streams, river pools, and connected mountain/reservoir water',
    feedingProfile: { primaryPrey: ['plants', 'insects', 'shrimp', 'mollusks'], notes: 'Fast rocky water and natural food drift are the key habitat/feeding cues.' },
    speciesKnowledge: {
      habitatFact: 'Deccan mahseer are associated with peninsular Indian cool, fast-flowing rocky streams and rivers and can also occur in mountain lakes or stocked reservoirs.',
      feedingFact: 'Recorded foods include plants, insects, shrimps, and mollusks.',
      tacticFact: 'Fish boulder seams, pool transitions, and current breaks with quiet approaches and natural-sized presentations.',
    },
    publicSourceRefs: ['FishBase Biology: Tor khudree'],
  },
  {
    ids: ['pearlspot_india', 'pearlspot'], scientificNames: ['Etroplus suratensis'],
    summary: 'A brackish-water specialist strongly associated with Indian coastal lagoons, estuaries, backwaters, and lower river/reservoir systems.',
    habitats: ['estuary', 'backwater', 'lagoon', 'river', 'reservoir'], waterRealms: ['brackish', 'freshwater'],
    bestBaits: ['Algae/Vegetable Bait', 'Dough Bait', 'Small Worm', 'Tiny Natural Bait'],
    bestTechniques: ['Fish slow backwater edges', 'Use small hooks and subtle bait', 'Target vegetation, roots, and protected banks'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'], activeTimes: ['dawn', 'day', 'dusk'],
    structureTags: ['backwater edges', 'vegetation', 'roots', 'slow current'], behaviorTags: ['grazing', 'brackish-water'],
    depthPreference: 'shallow-to-mid protected backwaters, lagoons, estuaries, and slow margins',
    feedingProfile: { primaryPrey: ['filamentous algae', 'plant material', 'insects'], notes: 'Small natural/plant presentations fit the species better than predator lures.' },
    speciesKnowledge: {
      habitatFact: 'Pearlspot are primarily brackish-water fish and are especially common in coastal backwaters and estuaries of southern and eastern India.',
      feedingFact: 'They feed on filamentous algae, plant material, and insects.',
      tacticFact: 'Use small hooks and plant/natural baits around protected vegetation, roots, and slow backwater margins.',
    },
    publicSourceRefs: ['FishBase Biology: Etroplus suratensis'],
  },
  {
    ids: ['barramundi', 'barramundi_india', 'apahap_barramundi'], scientificNames: ['Lates calcarifer'],
    summary: 'A diadromous predator of estuaries, lagoons, coastal water, tidal rivers, and connected freshwater reaches, strongly associated with cover and current.',
    habitats: ['estuary', 'bay', 'inshore', 'river', 'backwater', 'mangrove'], waterRealms: ['freshwater', 'brackish', 'saltwater'],
    bestBaits: ['Live Baitfish', 'Paddle Tail', 'Minnow Plug', 'Shrimp/Prawn'],
    bestTechniques: ['Work current past cover', 'Cast snags and undercut banks', 'Fish river mouths and tidal drains'],
    bestTimesOfDay: ['dawn', 'dusk', 'night'], activeTimes: ['dawn', 'dusk', 'night'],
    structureTags: ['submerged logs', 'undercut banks', 'mangroves', 'river mouths', 'drains'], behaviorTags: ['ambush predator', 'diadromous'],
    depthPreference: 'cover-oriented estuary/river edges and current lanes, commonly shallow-to-mid water',
    feedingProfile: { primaryPrey: ['fish', 'crustaceans', 'shrimp'], notes: 'Cover plus current plus forage is the strongest practical catchability stack.' },
    speciesKnowledge: {
      habitatFact: 'Barramundi use coastal waters, estuaries, lagoons, and rivers; juveniles may occupy brackish swamps and upper river reaches, often around logs, undercut banks, and overhanging cover.',
      feedingFact: 'They feed primarily on fishes and crustaceans; juveniles also take insects.',
      tacticFact: 'Cast tight to structure where current delivers prey, especially snags, mangroves, drains, bridge shade, and river mouths.',
    },
    publicSourceRefs: ['FishBase Biology: Lates calcarifer'],
  },
  {
    ids: ['mangrove_jack', 'mangrove_jack_india', 'mangrove_jack_sea'], scientificNames: ['Lutjanus argentimaculatus'],
    summary: 'A structure-bound snapper: juveniles and young adults use mangrove estuaries, tidal creeks, and lower freshwater reaches, while adults shift toward reefs and deeper structure.',
    habitats: ['estuary', 'mangrove', 'river', 'bay', 'inshore', 'reef'], waterRealms: ['freshwater', 'brackish', 'saltwater'],
    bestBaits: ['Live Baitfish', 'Minnow Plug', 'Paddle Tail', 'Shrimp/Prawn'],
    bestTechniques: ['Cast tight to mangrove/snags', 'Fish current-facing structure', 'Work reef ledges after dark/low light'],
    bestTimesOfDay: ['dusk', 'night', 'dawn'], activeTimes: ['dusk', 'night', 'dawn'],
    structureTags: ['mangroves', 'snags', 'tidal creeks', 'reef ledges', 'caves'], behaviorTags: ['nocturnal', 'ambush predator'],
    depthPreference: 'tight structure from shallow estuary cover to deeper reef ledges',
    feedingProfile: { primaryPrey: ['fish', 'crustaceans'], notes: 'Mainly nocturnal; shelter, moving water, and prey concentration are key.' },
    speciesKnowledge: {
      habitatFact: 'Young fish use mangrove estuaries, lower freshwater streams, and tidal creeks; adults commonly occupy coral reefs and deeper reef structure.',
      feedingFact: 'Mangrove jack feed mostly on fishes and crustaceans.',
      timingFact: 'They are mainly nocturnal, so dusk, night, and dawn deserve more weight than midday.',
      tacticFact: 'Put the presentation tight to shelter and use current to sweep it through the ambush lane.',
    },
    publicSourceRefs: ['FishBase Ecology: Lutjanus argentimaculatus'],
  },
  {
    ids: ['giant_trevally', 'giant_trevally_sea', 'giant_trevally_india'], scientificNames: ['Caranx ignobilis'],
    summary: 'A powerful inshore/reef predator of clear lagoons, seaward reefs, passes, and current-fed edges; juveniles also use estuaries.',
    habitats: ['reef', 'inshore', 'bay', 'estuary', 'ocean'], waterRealms: ['brackish', 'saltwater'],
    bestBaits: ['Stickbait', 'Popping Plug', 'Live Baitfish', 'Metal Jig'],
    bestTechniques: ['Cast current-washed reef points', 'Work passes and bait schools', 'Fish low light and night where safe'],
    bestTimesOfDay: ['night', 'dawn', 'dusk', 'day'], activeTimes: ['night', 'dawn', 'dusk', 'day'],
    structureTags: ['reef points', 'passes', 'current seams', 'bait schools', 'lagoon edges'], behaviorTags: ['roaming predator', 'primarily nocturnal feeder'],
    depthPreference: 'surface-to-mid around reef/lagoon edges, passes, and current-fed bait lanes',
    feedingProfile: { primaryPrey: ['fish', 'crabs', 'spiny lobsters'], notes: 'Primarily nocturnal feeding is documented, though fish also feed during daylight.' },
    speciesKnowledge: {
      habitatFact: 'Adults commonly use clear lagoons and seaward reefs; juveniles may use estuaries.',
      feedingFact: 'GT feed on fishes and crustaceans including crabs and lobsters.',
      timingFact: 'Night is a documented primary feeding period, with some daytime feeding as well.',
      tacticFact: 'Prioritize moving water, reef points, passes, and visible bait; use surface plugs or jigs based on prey depth.',
    },
    publicSourceRefs: ['FishBase Ecology: Caranx ignobilis'],
  },
  {
    ids: ['milkfish', 'milkfish_sea'], scientificNames: ['Chanos chanos'],
    summary: 'A schooling coastal/brackish fish that enters bays and estuaries and can also occur in freshwater; adults and juveniles graze algae and take small benthic invertebrates.',
    habitats: ['bay', 'estuary', 'lagoon', 'inshore', 'pond'], waterRealms: ['freshwater', 'brackish', 'saltwater'],
    bestBaits: ['Bread', 'Dough Bait', 'Algae/Plant Bait', 'Tiny Natural Bait'],
    bestTechniques: ['Use very small subtle presentations', 'Fish visible schools and protected flats', 'Avoid spooking shallow fish'],
    bestTimesOfDay: ['day', 'dawn', 'dusk'], activeTimes: ['day', 'dawn', 'dusk'],
    structureTags: ['protected flats', 'estuaries', 'coastal wetlands', 'shallow bays'], behaviorTags: ['schooling', 'grazing'],
    depthPreference: 'surface-to-shallow protected coastal, estuary, and pond water',
    feedingProfile: { primaryPrey: ['algae', 'cyanobacteria', 'zooplankton', 'small benthic invertebrates'], notes: 'Subtle plant/natural presentations fit the species feeding ecology better than fast predator lures.' },
    speciesKnowledge: {
      habitatFact: 'Milkfish use coastal water, bays, and estuaries, and can occasionally enter freshwater; they often form schools near the surface.',
      feedingFact: 'They feed on algae plus zooplankton and small benthic invertebrates.',
      tacticFact: 'Approach shallow schools quietly and use tiny, subtle plant/natural presentations rather than aggressive predator lures.',
    },
    publicSourceRefs: ['FishBase Ecology: Chanos chanos'],
  },
  {
    ids: ['striped_snakehead', 'mudfish_dalag', 'striped_snakehead_indonesia'], scientificNames: ['Channa striata'],
    summary: 'A freshwater snakehead of rivers, creeks, flooded fields, swamps, ponds, and sluggish canals; it is strongly cover-oriented and often feeds after dark/low light.',
    habitats: ['river', 'creek', 'pond', 'canal', 'marsh', 'lake'], waterRealms: ['freshwater'],
    bestBaits: ['Topwater Frog', 'Soft Plastic', 'Minnow Plug', 'Small Live Bait'],
    bestTechniques: ['Work weeds and flooded cover', 'Probe canal/drain mouths', 'Pause beside openings in surface cover'],
    bestTimesOfDay: ['night', 'dusk', 'dawn'], activeTimes: ['night', 'dusk', 'dawn'],
    structureTags: ['aquatic vegetation', 'flooded margins', 'canal edges', 'swamps', 'drains'], behaviorTags: ['nocturnal', 'ambush predator', 'air-breathing'],
    depthPreference: 'shallow vegetated margins, flooded cover, canals, and slow-water ambush lanes',
    feedingProfile: { primaryPrey: ['fish', 'frogs', 'insects', 'worms', 'crustaceans'], notes: 'Philippine records describe nocturnal behavior; cover and prey-rich shallow water are strong practical cues.' },
    speciesKnowledge: {
      habitatFact: 'Striped snakehead occur in rivers, brooks, flooded fields, stagnant water, swamps, and sluggish canals.',
      feedingFact: 'Adults take fish, frogs, insects, worms, and crustaceans.',
      timingFact: 'Low light and night are strong general activity windows in documented Philippine populations.',
      tacticFact: 'Fish slowly and tight to vegetation, flooded edges, drains, and surface openings where prey is concentrated.',
    },
    publicSourceRefs: ['FishBase Philippines: Channa striata', 'FishBase Diet: Channa striata'],
  },
  {
    ids: ['hampala_barb'], scientificNames: ['Hampala macrolepidota'],
    summary: 'A predatory freshwater barb of rivers and lakes that favors slower-flowing water and feeds on fish, prawns, insects, and insect larvae.',
    habitats: ['river', 'lake', 'reservoir', 'creek'], waterRealms: ['freshwater'],
    bestBaits: ['Minnow Plug', 'Small Crankbait', 'Inline Spinner', 'Spoon'],
    bestTechniques: ['Cast slow-current seams', 'Work rocky runs and points', 'Follow baitfish in lakes/reservoirs'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'], activeTimes: ['dawn', 'day', 'dusk'],
    structureTags: ['slow current', 'rocks', 'points', 'baitfish lanes'], behaviorTags: ['predator', 'current-aware feeder'],
    depthPreference: 'slow-flowing river/lake zones and current transitions',
    feedingProfile: { primaryPrey: ['fish', 'prawns', 'insects', 'insect larvae'], notes: 'Fish becomes increasingly piscivorous as it grows; baitfish presence is a useful confidence signal.' },
    speciesKnowledge: {
      habitatFact: 'Hampala macrolepidota lives in rivers and lakes and is reported to prefer slower-flowing water.',
      feedingFact: 'Its diet includes fish, prawns, insects, and insect larvae.',
      tacticFact: 'Use compact baitfish imitations through slow-current seams, rocky runs, and lake/reservoir points where forage is present.',
    },
    publicSourceRefs: ['FishBase Ecology: Hampala macrolepidota'],
  },
  {
    ids: ['baung_catfish_indonesia'], scientificNames: ['Hemibagrus nemurus'],
    summary: 'A tropical bagrid catfish most often associated with large muddy rivers, slow current, soft bottom, and flooded forest connections.',
    habitats: ['river', 'lake'], waterRealms: ['freshwater', 'brackish'],
    bestBaits: ['Shrimp/Prawn', 'Cut Bait', 'Worm', 'Small Live Bait'],
    bestTechniques: ['Bottom fish slow current', 'Target muddy channel edges', 'Fish flooded-forest/current connections'],
    bestTimesOfDay: ['dusk', 'night', 'dawn'], activeTimes: ['dusk', 'night', 'dawn'],
    structureTags: ['muddy river', 'soft bottom', 'slow current', 'flooded forest'], behaviorTags: ['predator', 'bottom-oriented'],
    depthPreference: 'bottom-oriented large river channels and slow-current soft-bottom areas',
    feedingProfile: { primaryPrey: ['insects', 'aquatic insect larvae', 'shrimp', 'crustaceans', 'fish'], notes: 'Large muddy rivers and slow current are stronger habitat cues than generic reservoirs.' },
    speciesKnowledge: {
      habitatFact: 'Hemibagrus nemurus is most frequent in large muddy rivers with slow current and soft bottom and may enter flooded forest.',
      feedingFact: 'It takes insects, aquatic larvae, shrimps, other crustaceans, and fish.',
      tacticFact: 'Keep bait near bottom along slow-current channel edges, soft-bottom bends, and flooded-forest connections.',
    },
    publicSourceRefs: ['FishBase Ecology: Hemibagrus nemurus'],
    geoBoxes: [
      { minLat: -6.5, maxLat: 5.8, minLon: 94.5, maxLon: 106.2 },
      { minLat: -8.8, maxLat: -5.7, minLon: 105.0, maxLon: 114.7 },
      { minLat: -4.5, maxLat: 7.5, minLon: 108.0, maxLon: 119.5 },
    ],
    geoPenaltyOutside: 28,
    geoReasonOutside: 'baung/Hemibagrus nemurus is strongest in western Indonesian drainages such as Sumatra, Java, and Borneo',
  },
  {
    ids: ['giant_snakehead_indonesia', 'giant_snakehead'], scientificNames: ['Channa micropeltes'],
    summary: 'A large freshwater snakehead native in Indonesia chiefly to Sumatra and Borneo, using lowland rivers, swamps, deep standing water, large streams, and slow canals.',
    habitats: ['river', 'swamp', 'canal', 'lake', 'reservoir'], waterRealms: ['freshwater'],
    bestBaits: ['Topwater Frog', 'Swimbait', 'Minnow Plug', 'Walking Bait'],
    bestTechniques: ['Work deep cover edges', 'Fish slow canals and large streams', 'Follow baitfish around vegetation'],
    bestTimesOfDay: ['dawn', 'dusk', 'night'], activeTimes: ['dawn', 'dusk', 'night'],
    structureTags: ['deep water', 'vegetation', 'slow canals', 'swamps', 'large streams'], behaviorTags: ['predatory', 'cover-oriented'],
    depthPreference: 'deeper lowland river/swamp water and slow channels near cover',
    feedingProfile: { primaryPrey: ['fish', 'crustaceans'], notes: 'Adults feed mainly on fish; broad open-water searching is less useful than cover, depth, and forage.' },
    speciesKnowledge: {
      habitatFact: 'In Indonesia the documented native range includes Sumatra and Borneo; adults use lowland rivers and swamps and are often associated with deeper standing or slow-flowing water.',
      feedingFact: 'Adults feed mainly on fish and also take crustaceans.',
      tacticFact: 'Prioritize deeper cover edges, slow channels, vegetation, and baitfish rather than treating every Indonesian freshwater body as equal habitat.',
    },
    publicSourceRefs: ['FishBase Biology: Channa micropeltes'],
    geoBoxes: [
      { minLat: -6.5, maxLat: 6.2, minLon: 94.0, maxLon: 106.5 },
      { minLat: -4.8, maxLat: 7.5, minLon: 108.0, maxLon: 119.5 },
    ],
    geoPenaltyOutside: 36,
    geoReasonOutside: 'giant snakehead/toman native-range confidence is strongest in Sumatra and Borneo',
  },
  {
    ids: ['nile_tilapia', 'nile_tilapia_indonesia', 'tilapia_india'], scientificNames: ['Oreochromis niloticus'],
    summary: 'A mainly diurnal warmwater tilapia of lakes, rivers, canals, irrigation channels, reservoirs, and ponds that grazes algae and plankton-rich food.',
    habitats: ['lake', 'river', 'canal', 'reservoir', 'pond'], waterRealms: ['freshwater', 'brackish'],
    bestBaits: ['Bread', 'Dough Bait', 'Worm', 'Small Plant/Natural Bait'],
    bestTechniques: ['Fish shallow feeding edges', 'Use small hooks and floats', 'Target algae/vegetation and warm protected water'],
    bestTimesOfDay: ['day', 'dawn', 'dusk'], activeTimes: ['day', 'dawn', 'dusk'],
    structureTags: ['shallow edges', 'vegetation', 'canals', 'beds'], behaviorTags: ['diurnal', 'grazing'],
    depthPreference: 'shallow-to-mid warmwater feeding edges and protected water',
    feedingProfile: { primaryPrey: ['phytoplankton', 'benthic algae', 'insect larvae', 'detritus'], notes: 'Mainly diurnal; small natural/plant baits and shallow feeding edges fit the ecology.' },
    speciesKnowledge: {
      habitatFact: 'Nile tilapia use a wide range of freshwater habitats including rivers, lakes, sewage canals, and irrigation channels and can tolerate some brackish water.',
      feedingFact: 'Adults feed mainly on phytoplankton and benthic algae, with insect larvae and detritus also important.',
      timingFact: 'They are mainly diurnal.',
      tacticFact: 'Use small baits and light tackle around warm shallow feeding edges, vegetation, and visible beds or schools.',
    },
    publicSourceRefs: ['FishBase Biology: Oreochromis niloticus'],
  },
  {
    ids: ['seer_fish_india', 'narrow_barred_spanish_mackerel'], scientificNames: ['Scomberomorus commerson'],
    summary: 'A fast coastal pelagic predator of shallow coastal water, shelf edges, reefs, lagoons, and drop-offs that hunts small schooling fishes, squid, and prawns.',
    habitats: ['inshore', 'ocean', 'reef', 'bay'], waterRealms: ['saltwater', 'brackish'],
    bestBaits: ['Trolling Minnow', 'Metal Jig', 'Live Baitfish', 'Spoon'],
    bestTechniques: ['Troll coastal bait lanes', 'Cast/jig drop-offs and reef edges', 'Follow schooling bait'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'], activeTimes: ['dawn', 'day', 'dusk'],
    structureTags: ['shelf edge', 'drop-offs', 'reef slopes', 'bait schools'], behaviorTags: ['pelagic predator', 'bait-tracker'],
    depthPreference: 'shallow coastal slopes through reef/drop-off and shelf-edge bait lanes',
    feedingProfile: { primaryPrey: ['small fish', 'squid', 'prawns'], notes: 'Bait schools and coastal current structure are stronger catchability cues than country-level presence.' },
    speciesKnowledge: {
      habitatFact: 'Narrow-barred Spanish mackerel inhabit shallow coastal water through shelf-edge, reef, lagoon, and drop-off habitats.',
      feedingFact: 'They feed primarily on small fishes and also take squid and penaeid shrimp.',
      tacticFact: 'Search moving bait along coastal slopes, reef edges, and drop-offs with trolling plugs or fast jigs.',
    },
    publicSourceRefs: ['FishBase Biology/Ecology: Scomberomorus commerson'],
  },
];

const matchesFact = (species: any, fact: CatchabilityFact): boolean => {
  const ids = new Set([species?.id, species?.slug, species?.canonicalSpeciesId, species?.speciesId].map(key).filter(Boolean));
  const scientific = key(species?.scientificName || species?.taxonomy?.scientificName);
  const names = new Set([species?.commonName, species?.name, ...(Array.isArray(species?.aliases) ? species.aliases : [])].map(key).filter(Boolean));

  if ((fact.ids || []).some((value) => ids.has(key(value)))) return true;
  if (scientific && (fact.scientificNames || []).some((value) => key(value) === scientific)) return true;
  return (fact.commonNames || []).some((value) => names.has(key(value)));
};

export function getFishIQCatchabilityFact(species: any): CatchabilityFact | null {
  return FACTS.find((fact) => matchesFact(species, fact)) || null;
}

export function applyFishIQCatchabilityFacts<T = any>(species: T): T {
  const raw: any = species || {};
  const fact = getFishIQCatchabilityFact(raw);
  if (!fact) return species;

  const merged: any = {
    ...raw,
    ...(fact.summary ? { summary: fact.summary, description: fact.summary } : {}),
    habitats: uniq([...(fact.habitats || []), ...(raw.habitats || [])]),
    waterRealms: uniq([...(fact.waterRealms || []), ...(raw.waterRealms || [])]),
    bestBaits: uniq([...(fact.bestBaits || []), ...(raw.bestBaits || [])]),
    bestTechniques: uniq([...(fact.bestTechniques || []), ...(raw.bestTechniques || [])]),
    bestTimesOfDay: uniq([...(fact.bestTimesOfDay || []), ...(raw.bestTimesOfDay || [])]),
    activeTimes: uniq([...(fact.activeTimes || []), ...(raw.activeTimes || [])]),
    structureTags: uniq([...(fact.structureTags || []), ...(raw.structureTags || [])]),
    behaviorTags: uniq([...(fact.behaviorTags || []), ...(raw.behaviorTags || [])]),
    depthPreference: fact.depthPreference || raw.depthPreference,
    feedingProfile: {
      ...(raw.feedingProfile || {}),
      ...(fact.feedingProfile || {}),
      primaryPrey: uniq([...(fact.feedingProfile?.primaryPrey || []), ...(raw.feedingProfile?.primaryPrey || [])]),
    },
    speciesKnowledge: {
      ...(raw.speciesKnowledge || {}),
      ...(fact.speciesKnowledge || {}),
    },
    publicSourceRefs: uniq([...(raw.publicSourceRefs || []), ...(fact.publicSourceRefs || [])]),
  };

  return merged as T;
}

const inBox = (lat: number, lon: number, box: GeoBox) =>
  lat >= box.minLat && lat <= box.maxLat && lon >= box.minLon && lon <= box.maxLon;

export function getFishIQCatchabilityGeoAdjustment(
  species: any,
  ctx: FishIQCatchabilityContext,
): { score: number; reason?: string } {
  const fact = getFishIQCatchabilityFact(species);
  if (!fact?.geoBoxes?.length || !fact.geoPenaltyOutside) return { score: 0 };

  const lat = Number(ctx?.lat);
  const lon = Number(ctx?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { score: 0 };

  if (fact.geoBoxes.some((box) => inBox(lat, lon, box))) return { score: 0 };
  return {
    score: -Math.abs(Number(fact.geoPenaltyOutside || 0)),
    reason: fact.geoReasonOutside || 'outside strongest documented regional range',
  };
}
